import { supabase } from '../lib/supabase';
import type { ImageAnalysis, Post, Recording } from '../types/database';

export interface HistoryItem {
  analysis: {
    id: string;
    image_url?: string;
    content?: string;
    analysis_style: string;
    questions: string[];
    created_at: string;
    context: {
      topic: string;
      industry: string;
      contentType: string;
      tone: string;
    };
  };
  recording?: {
    id: string;
    created_at: string;
  };
  post?: {
    id: string;
    content: string;
    edited_content?: string;
    status?: 'draft' | 'published';
  };
}

export async function fetchUserHistory(userId: string): Promise<HistoryItem[]> {
  // Fetch image analyses
  const { data: imageAnalyses, error: imageError } = await supabase
    .from('image_analyses')
    .select(`
      *,
      posts!posts_analysis_id_fkey (
        *,
        recordings (*)
      )
    `)
    .eq('user_id', userId);

  if (imageError) {
    console.error('Error fetching image analyses:', imageError);
  }

  // Fetch text analyses
  const { data: textAnalyses, error: textError } = await supabase
    .from('text_analyses')
    .select(`
      *,
      posts!posts_text_analysis_id_fkey (
        *,
        recordings (*)
      )
    `)
    .eq('user_id', userId);

  if (textError) {
    console.error('Error fetching text analyses:', textError);
  }

  // Transform and combine both types of analyses
  const imageItems = (imageAnalyses || []).map((analysis: any) => {
    const post = analysis.posts?.[0];
    const recording = post?.recordings?.[0];

    return {
      analysis: {
        id: analysis.id,
        image_url: analysis.image_url,
        analysis_style: analysis.analysis_style,
        questions: Array.isArray(analysis.questions) 
          ? analysis.questions 
          : typeof analysis.questions === 'string'
            ? JSON.parse(analysis.questions)
            : [],
        created_at: analysis.created_at,
        context: {
          topic: analysis.topic || '',
          industry: analysis.industry || '',
          contentType: analysis.content_type || '',
          tone: analysis.tone || '',
        },
      },
      post: post ? {
        id: post.id,
        content: post.content,
        edited_content: post.edited_content,
        status: post.status,
      } : undefined,
      recording: recording ? {
        id: recording.id,
        created_at: recording.created_at,
      } : undefined,
    };
  });

  const textItems = (textAnalyses || []).map((analysis: any) => {
    const post = analysis.posts?.[0];
    const recording = post?.recordings?.[0];

    return {
      analysis: {
        id: analysis.id,
        content: analysis.content,
        analysis_style: analysis.analysis_style,
        questions: Array.isArray(analysis.questions) 
          ? analysis.questions 
          : typeof analysis.questions === 'string'
            ? JSON.parse(analysis.questions)
            : [],
        created_at: analysis.created_at,
        context: analysis.context || {
          topic: '',
          industry: '',
          contentType: '',
          tone: '',
        },
      },
      post: post ? {
        id: post.id,
        content: post.content,
        edited_content: post.edited_content,
        status: post.status,
      } : undefined,
      recording: recording ? {
        id: recording.id,
        created_at: recording.created_at,
      } : undefined,
    };
  });

  // Combine and sort by created_at date
  return [...imageItems, ...textItems]
    .sort((a, b) => 
      new Date(b.analysis.created_at).getTime() - new Date(a.analysis.created_at).getTime()
    );
}
